import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CakeIcon from '@mui/icons-material/Cake';
import UserAvatar from '../util/UserAvatar';

interface BirthdayUser {
  id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  avatarUrl?: string;
  daysUntil: number;
}

interface Props { auth: boolean; baseUrl: string; }

function formatDate(dob: string): string {
  const d = new Date(dob);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function daysLabel(days: number): { text: string; color: string } {
  if (days === 0) return { text: 'Today! 🎉', color: '#f4a942' };
  if (days === 1) return { text: 'Tomorrow', color: '#43c98a' };
  if (days <= 7) return { text: `In ${days} days`, color: '#4f8ef7' };
  return { text: `In ${days} days`, color: 'var(--text-color-calendar-week)' };
}

const BirthdaysCard: React.FC<Props> = ({ auth, baseUrl }) => {
  const [users, setUsers] = useState<BirthdayUser[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get('users/birthdays');
      setUsers(data);
    } catch { /* interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (auth) fetch(); }, [auth, fetch]);

  return (
    <div className="dashboard-card" style={{ padding: '14px 14px 10px', minWidth: 220, height: 300, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12, flexShrink: 0 }}>
        <CakeIcon style={{ fontSize: 16, color: '#f4a942' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Upcoming Birthdays
        </span>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="skeleton-line" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div className="skeleton-line" style={{ width: '65%', height: 12 }} />
                <div className="skeleton-line" style={{ width: '40%', height: 10 }} />
              </div>
              <div className="skeleton-line" style={{ width: 50, height: 12 }} />
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: '8px 0' }}>No birthdays on file</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {users.map(u => {
            const { text, color } = daysLabel(u.daysUntil);
            return (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Avatar */}
                <UserAvatar user={u} baseUrl={baseUrl} size={28} />
                {/* Name + date */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.first_name} {u.last_name}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                    {formatDate(u.date_of_birth)}
                  </div>
                </div>
                {/* Days badge */}
                <span style={{ fontSize: 10, fontWeight: 600, color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {text}
                </span>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default BirthdaysCard;
